// src/admin/pages/AdminPatronat.jsx
import React, { useState, useEffect } from "react";
import { patronatService } from "../../../services/api";
import { loadPatronatInfo, updatePatronatInfo } from "../../../backend/info/patronatInfo";
import TabBlockView from "../../components/TabBlockView";
import { toast } from "react-toastify";

// ─── Patronat CRUD Tab ────────────────────────────────────────────────────────
const PatronatTab = () => {
  const [patronat, setPatronat] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState({
    name: "", text: "", buttonText: "", buttonLink: "", navigationState: "",
  });
  const [currentId, setCurrentId] = useState(null);

  useEffect(() => { fetchPatronat(); }, []);

  const fetchPatronat = async () => {
    try {
      setLoading(true);
      const response = await patronatService.getAll();
      setPatronat(response.data);
    } catch (err) {
      setError("Не вдалося завантажити дані патронату");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ name: "", text: "", buttonText: "", buttonLink: "", navigationState: "" });
    setFormMode("add");
    setCurrentId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formMode === "add") {
        await patronatService.create(formData);
        toast.success("Запис успішно додано!");
      } else {
        await patronatService.update(currentId, formData);
        toast.success("Запис успішно оновлено!");
      }
      resetForm();
      fetchPatronat();
    } catch {
      toast.error(`Помилка при ${formMode === "add" ? "додаванні" : "оновленні"} запису`);
    }
  };

  const handleEdit = (item) => {
    setFormData({
      name: item.name, text: item.text, buttonText: item.buttonText,
      buttonLink: item.buttonLink, navigationState: item.navigationState,
    });
    setCurrentId(item.id);
    setFormMode("edit");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Ви впевнені, що хочете видалити цей запис?")) return;
    try {
      await patronatService.delete(id);
      setPatronat((prev) => prev.filter((item) => item.id !== id));
      toast.success("Запис успішно видалено!");
    } catch {
      toast.error("Помилка при видаленні запису");
    }
  };

  const swapItems = async (index, direction) => {
    const other = direction === "up" ? index - 1 : index + 1;
    if (other < 0 || other >= patronat.length) return;
    try {
      const updated = [...patronat];
      const a = updated[index], b = updated[other];
      const ua = { ...a, id: b.id }, ub = { ...b, id: a.id };
      updated[index] = ub; updated[other] = ua;
      setPatronat(updated);
      await Promise.all([patronatService.update(a.id, ub), patronatService.update(b.id, ua)]);
      toast.success("Порядок успішно змінено!");
    } catch {
      toast.error("Помилка при зміні порядку");
      fetchPatronat();
    }
  };

  if (loading) return <p style={styles.loadingText}>Завантаження...</p>;
  if (error) return <p style={styles.errorText}>{error}</p>;

  return (
    <div>
      <div style={styles.formContainer}>
        <h2 style={styles.formTitle}>
          {formMode === "add" ? "Додати новий запис" : "Редагувати запис"}
        </h2>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="p-name">Назва:</label>
            <input style={styles.input} type="text" id="p-name" name="name"
              placeholder="Наприклад: Підтримка військових" value={formData.name}
              onChange={handleChange} required />
          </div>

          <div style={styles.formGroupFullWidth}>
            <label style={styles.label} htmlFor="p-text">Текст опису:</label>
            <textarea style={styles.textArea} id="p-text" name="text"
              placeholder="Детальний опис патронату..." value={formData.text}
              onChange={handleChange} required />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="p-buttonText">Текст кнопки:</label>
            <input style={styles.input} type="text" id="p-buttonText" name="buttonText"
              placeholder="Наприклад: Підтримати" value={formData.buttonText}
              onChange={handleChange} required />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="p-buttonLink">Посилання кнопки:</label>
            <input style={styles.input} type="url" id="p-buttonLink" name="buttonLink"
              placeholder="https://example.com" value={formData.buttonLink}
              onChange={handleChange} required />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="p-navigationState">Стан навігації:</label>
            <input style={styles.input} type="text" id="p-navigationState" name="navigationState"
              placeholder="Наприклад: donation" value={formData.navigationState}
              onChange={handleChange} required />
          </div>

          <div style={styles.buttonContainer}>
            {formMode === "edit" && (
              <button type="button" style={styles.cancelButton} onClick={resetForm}>Скасувати</button>
            )}
            <button type="submit" style={styles.button}>
              {formMode === "add" ? "Додати" : "Зберегти зміни"}
            </button>
          </div>
        </form>
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.tableHeader}>Порядок</th>
              <th style={styles.tableHeader}>Назва</th>
              <th style={styles.tableHeader}>Текст</th>
              <th style={styles.tableHeader}>Кнопка</th>
              <th style={styles.tableHeader}>Посилання</th>
              <th style={styles.tableHeader}>Стан навігації</th>
              <th style={styles.tableHeader}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {patronat.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: "center" }}>Немає доданих записів</td></tr>
            ) : (
              patronat.map((item, index) => (
                <tr key={item.id} style={styles.tableRow}>
                  <td style={styles.tableCell}>
                    <div style={styles.positionButtonsContainer}>
                      <button style={styles.positionButton} onClick={() => swapItems(index, "up")} disabled={index === 0} title="Вгору">▲</button>
                      <button style={styles.positionButton} onClick={() => swapItems(index, "down")} disabled={index === patronat.length - 1} title="Вниз">▼</button>
                    </div>
                  </td>
                  <td style={styles.tableCell}>{item.name}</td>
                  <td style={styles.tableCell}>
                    <div style={styles.textPreview}>
                      {item.text.length > 100 ? `${item.text.substring(0, 100)}...` : item.text}
                    </div>
                  </td>
                  <td style={styles.tableCell}>{item.buttonText}</td>
                  <td style={styles.tableCell}>
                    <a href={item.buttonLink} target="_blank" rel="noopener noreferrer" style={styles.linkPreview}>
                      {item.buttonLink.length > 30 ? `${item.buttonLink.substring(0, 30)}...` : item.buttonLink}
                    </a>
                  </td>
                  <td style={styles.tableCell}>{item.navigationState}</td>
                  <td style={styles.tableCell}>
                    <a style={styles.actionLink} onClick={() => handleEdit(item)}>Редагувати</a>
                    <a style={styles.actionLinkDanger} onClick={() => handleDelete(item.id)}>Видалити</a>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Patronat Info Tab ────────────────────────────────────────────────────────
const FIELDS = [
  { name: "h1",    label: "Заголовок сторінки",        placeholder: "Введіть заголовок",   type: "textarea" },
  { name: "h1mobile",    label: "Заголовок сторінки (для мобільних)",        placeholder: "Введіть заголовок",   type: "textarea" },
  { name: "h2",    label: "Підзаголовок сторінки",        placeholder: "Введіть підзаголовок",   type: "textarea" },
  { name: "metaTitle",    label: "Заголовок (для СЕО, не показується користувачу)",        placeholder: "Введіть СЕО заголовок",   type: "textarea" },
  { name: "metaDescription",    label: "Опис (для СЕО, не показується користувачу)",        placeholder: "Введіть СЕО опис",   type: "textarea" },
  { name: "missionTop",    label: "Місія (верхня частина)",        placeholder: "Введіть верхню частину місії організації", type: "textarea" },
  { name: "missionBottom", label: "Місія (нижня частина)",         placeholder: "Введіть нижню частину місії організації", type: "textarea" },
  { name: "phone1",        label: "Телефон 1",                     placeholder: "+38 (XXX) XXX-XX-XX",                     type: "tel" },
  { name: "phone2",        label: "Телефон 2",                     placeholder: "+38 (XXX) XXX-XX-XX",                     type: "tel" },
  { name: "email",         label: "Email",                         placeholder: "contact@example.com",                     type: "email" },
  { name: "supportLink",   label: "Посилання на підтримку",        placeholder: "https://example.com/support",             type: "url" },
  { name: "feedbackLink",  label: "Посилання на зворотний зв'язок", placeholder: "https://example.com/feedback",           type: "url" },
];

const EMPTY = Object.fromEntries(FIELDS.map((f) => [f.name, ""]));

const PatronatInfoTab = () => {
  const [formData, setFormData]       = useState({ ...EMPTY });
  const [originalData, setOriginalData] = useState({ ...EMPTY });
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);

  const fetchInfo = async () => {
    try {
      setLoading(true);
      const data = await loadPatronatInfo();
      const obj = { ...EMPTY };
      data.forEach((item) => { obj[item.name] = item.text; });
      setFormData({ ...obj });
      setOriginalData({ ...obj });
    } catch (err) {
      setError(err.message || "Не вдалося завантажити інформацію патронату");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInfo(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const changed = Object.entries(formData).filter(([k, v]) => originalData[k] !== v);
    if (changed.length === 0) { toast.info("Немає змін для збереження"); return; }
    try {
      await Promise.all(changed.map(([name, text]) => updatePatronatInfo(name, { text })));
      setOriginalData({ ...formData });
      toast.success("Інформацію патронату успішно збережено!");
    } catch (err) {
      toast.error(err.message || "Помилка при збереженні інформації");
    }
  };

  const reset = () => {
    setFormData({ ...originalData });
    toast.info("Форму скинуто до збережених значень");
  };

  if (loading) return <p style={styles.loadingText}>Завантаження...</p>;
  if (error) return (
    <div style={styles.errorContainer}>
      <p style={styles.errorText}>{error}</p>
      <button style={styles.retryButton} onClick={fetchInfo}>Спробувати знову</button>
    </div>
  );

  return (
    <div style={styles.formContainer}>
      <form onSubmit={handleSubmit} style={styles.form}>
        {FIELDS.map(({ name, label, placeholder, type }) => (
          <div key={name} style={type === "textarea" ? styles.formGroupFullWidth : styles.formGroup}>
            <label style={styles.label} htmlFor={`pi-${name}`}>{label}:</label>
            {type === "textarea" ? (
              <textarea style={styles.textArea} id={`pi-${name}`} name={name}
                placeholder={placeholder} value={formData[name] || ""}
                onChange={handleChange} />
            ) : (
              <input style={styles.input} type={type} id={`pi-${name}`} name={name}
                placeholder={placeholder} value={formData[name] || ""}
                onChange={handleChange} />
            )}
          </div>
        ))}
        <div style={styles.buttonContainer}>
          <button type="button" style={styles.resetButton} onClick={reset}>Скинути</button>
          <button type="submit" style={styles.button}>Зберегти інформацію</button>
        </div>
      </form>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminPatronat = () => (
  <div style={styles.pageContainer}>
    <h1 style={styles.pageTitle}>Патронат</h1>
    <TabBlockView
      tabs={[
        { name: "Записи",      content: <PatronatTab /> },
        { name: "Інформація",  content: <PatronatInfoTab /> },
      ]}
    />
  </div>
);

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  pageContainer: {
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
    padding: "2rem",
  },
  pageTitle: {
    marginBottom: "1.5rem",
    fontSize: "1.8rem",
    color: "var(--secondary-color)",
  },
  formContainer: {
    marginBottom: "2rem",
    padding: "1.5rem",
    backgroundColor: "#e9e9e9",
    borderRadius: "8px",
  },
  formTitle: {
    marginBottom: "1rem",
    fontSize: "1.3rem",
    color: "var(--secondary-color)",
  },
  form: {
    display: "grid",
    gap: "1rem",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  },
  formGroup: { display: "flex", flexDirection: "column" },
  formGroupFullWidth: { display: "flex", flexDirection: "column", gridColumn: "1 / -1" },
  label: { marginBottom: "0.5rem", fontWeight: 600 },
  input: {
    padding: "0.8rem",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    outline: "none",
  },
  textArea: {
    padding: "0.8rem",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    minHeight: "100px",
    resize: "vertical",
    outline: "none",
  },
  button: {
    padding: "0.8rem 1.5rem",
    backgroundColor: "green",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    fontWeight: 500,
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  cancelButton: {
    padding: "0.8rem 1.5rem",
    backgroundColor: "#FFBB00",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    fontWeight: 500,
    cursor: "pointer",
    transition: "background-color 0.3s",
    marginRight: "1rem",
  },
  resetButton: {
    padding: "0.8rem 1.5rem",
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    fontWeight: 500,
    cursor: "pointer",
    transition: "background-color 0.3s",
    marginRight: "1rem",
  },
  buttonContainer: { display: "flex", justifyContent: "flex-end", gridColumn: "1 / -1" },
  tableContainer: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  tableHeader: {
    padding: "0.8rem",
    textAlign: "left",
    borderBottom: "1px solid #eee",
    backgroundColor: "#e8e8e8",
    fontWeight: 600,
  },
  tableRow: { borderBottom: "1px solid #eee" },
  tableCell: { padding: "0.8rem", textAlign: "left", borderBottom: "1px solid #eee" },
  actionLink: { color: "var(--primary-color)", textDecoration: "none", cursor: "pointer", marginRight: "1rem" },
  actionLinkDanger: { color: "var(--error-color)", textDecoration: "none", cursor: "pointer", marginRight: "1rem" },
  loadingText: { padding: "2rem", textAlign: "center", color: "#666" },
  errorContainer: { display: "flex", flexDirection: "column", alignItems: "center", minHeight: "200px", justifyContent: "center" },
  errorText: { color: "#dc3545", marginBottom: "1rem", fontSize: "1.1rem" },
  retryButton: {
    padding: "0.8rem 1.5rem",
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    fontWeight: 500,
    cursor: "pointer",
  },
  positionButtonsContainer: { display: "flex", flexDirection: "column", gap: "0.5rem" },
  positionButton: {
    width: "30px", height: "30px", display: "flex", justifyContent: "center", alignItems: "center",
    backgroundColor: "#f0f0f0", border: "1px solid #ddd", borderRadius: "4px",
    cursor: "pointer", fontSize: "12px", padding: 0, lineHeight: 1,
  },
  textPreview: { maxWidth: "200px", wordWrap: "break-word" },
  linkPreview: { color: "var(--primary-color)", textDecoration: "none", maxWidth: "150px", display: "block", wordWrap: "break-word" },
};

export default AdminPatronat;